import React from "react"
import { motion } from "framer-motion"
import { Apple, Play } from "lucide-react"

export default function DownloadApp() {
  return (
    <section className="relative overflow-hidden bg-brand-primary pt-24 pb-0 lg:pt-32 lg:pb-0">
      {/* Dynamic Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-[300px] -right-[300px] w-[800px] h-[800px] bg-white/10 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-[300px] -left-[300px] w-[800px] h-[800px] bg-brand-secondary/20 rounded-full blur-[120px]"></div>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Content */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-8 pb-24 lg:pb-32"
          >
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
                Home services, <br />now in your pocket.
              </h2>
              <p className="text-lg md:text-xl text-white/80 max-w-lg leading-relaxed">
                Download the Hazer app to book professionals, track real-time progress, and manage your payments securely from anywhere.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button className="flex items-center justify-center gap-3 bg-brand-text text-white px-8 h-[60px] rounded-[16px] hover:bg-black transition-colors shadow-premium">
                <Apple className="w-8 h-8" />
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">Download on the</p>
                  <p className="text-lg font-bold leading-none">App Store</p>
                </div>
              </button>
              
              <button className="flex items-center justify-center gap-3 bg-white text-brand-text px-8 h-[60px] rounded-[16px] hover:bg-slate-50 transition-colors shadow-premium">
                <Play className="w-8 h-8 text-brand-primary" />
                <div className="text-left">
                  <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80 text-brand-muted">Get it on</p>
                  <p className="text-lg font-bold leading-none">Google Play</p>
                </div>
              </button>
            </div>

            <div className="flex items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <img key={i} className="w-12 h-12 rounded-full border-4 border-brand-primary" src={`https://i.pravatar.cc/100?img=${i + 20}`} alt="User" />
                ))}
              </div>
              <div>
                <div className="flex gap-1 text-yellow-400 mb-1">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
                </div>
                <p className="text-sm text-white/80 font-medium">Over 500k+ downloads</p>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Phone Mockup */}
          <div className="relative h-[500px] lg:h-[700px] flex justify-center lg:justify-end items-end">
            <motion.div 
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              className="relative w-[300px] md:w-[350px] lg:w-[400px] z-10"
            >
              {/* Phone Frame Mockup */}
              <div className="relative rounded-[48px] border-[12px] border-brand-text bg-white shadow-2xl overflow-hidden aspect-[1/2] translate-y-12">
                <div className="absolute top-0 inset-x-0 h-7 bg-brand-text rounded-b-3xl w-1/2 mx-auto z-20"></div>
                
                {/* App UI Mockup Inside Phone */}
                <div className="absolute inset-0 bg-brand-surface flex flex-col">
                  <div className="h-24 bg-brand-primary p-6 pt-10 text-white rounded-b-3xl shadow-sm z-10">
                    <p className="text-sm font-medium opacity-80">Good Morning,</p>
                    <p className="text-xl font-bold">Ahmed</p>
                  </div>
                  
                  <div className="flex-1 p-6 space-y-4">
                    <div className="h-12 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center px-4">
                      <div className="w-5 h-5 rounded-full bg-slate-200"></div>
                      <div className="h-2 w-24 bg-slate-200 rounded ml-3"></div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className="aspect-square bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-col items-center justify-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-brand-primary/10"></div>
                          <div className="h-2 w-16 bg-slate-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Floating Element Behind Phone */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="absolute right-0 bottom-48 w-48 h-48 bg-brand-secondary rounded-full mix-blend-multiply filter blur-3xl opacity-50 z-0"
            ></motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}

function StarIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
    </svg>
  )
}
